import { apiClient } from '@/lib/api/client'
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase'
import type { User } from '@/types/entities'

/**
 * Sign-in through Supabase, the identity provider for the platform.
 *
 * One login, one form. An artist types their email and password and this decides which issuer
 * they belong to — asking them "are you a new account or an old one?" would be asking them to know
 * something about our migration, which is not their problem. Supabase is tried first; a legacy
 * artist has no Supabase account, that attempt fails, and the caller falls back to the existing
 * AMS signin. Whoever they are, the same screen works.
 *
 * The access token goes into the same authStore the legacy token uses, so every existing API call
 * carries it unchanged — the API accepts either issuer.
 */

/** Providers wired in the UI. Widening this is the code half of adding one. */
export type SsoProvider = 'google' | 'facebook'

export interface SupabaseSignInResult {
  user: User
  accessToken: string
}

/** Supabase's own error for "these credentials do not match an account here". */
function isCredentialFailure(message: string): boolean {
  return /invalid login credentials|invalid email or password/i.test(message)
}

/**
 * Supabase authenticated the person, but no artist here matches that identity.
 *
 * Distinct from NoSupabaseAccountError because it must NOT fall through to the legacy login:
 * the password was right, and retrying it would report "wrong password" for what is really an
 * unlinked account.
 */
export class SupabaseIdentityUnknownError extends Error {
  constructor() {
    super('Signed in, but this identity is not linked to an artist account')
    this.name = 'SupabaseIdentityUnknownError'
  }
}

export class NoSupabaseAccountError extends Error {
  constructor() {
    super('No Supabase account for these credentials')
    this.name = 'NoSupabaseAccountError'
  }
}

/**
 * Exchange a Supabase session for the platform user behind it.
 *
 * The API resolves the token to an AMS artist; an identity that matches no artist gets no session,
 * which is why this can fail even though the password was right.
 */
async function loadPlatformUser(accessToken: string): Promise<User> {
  const response = await apiClient.get<User>('/api/auth', {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  return response.data
}

export const supabaseAuth = {
  isConfigured: () => isSupabaseConfigured,

  async signIn(email: string, password: string): Promise<SupabaseSignInResult> {
    const supabase = getSupabase()
    if (!supabase) throw new NoSupabaseAccountError()

    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      // Not "wrong password" — "not an account on this issuer". The caller tries the legacy path.
      if (isCredentialFailure(error.message)) throw new NoSupabaseAccountError()
      throw error
    }
    const accessToken = data.session?.access_token
    if (!accessToken) throw new NoSupabaseAccountError()

    try {
      return { user: await loadPlatformUser(accessToken), accessToken }
    } catch (e) {
      throw new SupabaseIdentityUnknownError()
    }
  },

  /**
   * Start a provider sign-in.
   *
   * Redirects away from the app; the browser comes back to /auth/callback with the session in the
   * URL fragment, which the Supabase client consumes on load (detectSessionInUrl). Adding a
   * further provider is enabling it in the Supabase dashboard and widening this union — no other
   * code changes, which is the whole reason for putting Supabase in front of this.
   */
  async signInWithProvider(provider: SsoProvider): Promise<void> {
    const supabase = getSupabase()
    if (!supabase) throw new Error('Supabase is not configured')
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    })
    if (error) throw error
  },

  /**
   * Send a password reset.
   *
   * Deliberately Supabase's flow rather than the platform's own. The legacy reset derives its
   * token from a column written once at signup and never rotated, so those links neither expire
   * nor stop working once used. Supabase's are expiring, single-use and rate-limited, and using
   * one moves the artist onto the new identity — the reset IS the migration.
   */
  async sendPasswordReset(email: string): Promise<void> {
    const supabase = getSupabase()
    if (!supabase) throw new Error('Supabase is not configured')
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset`,
    })
    if (error) throw error
  },

  /** Set a new password for the session a reset link established. */
  async completePasswordReset(password: string): Promise<string> {
    const supabase = getSupabase()
    if (!supabase) throw new Error('Supabase is not configured')
    const { data, error } = await supabase.auth.updateUser({ password })
    if (error) throw error
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData.session?.access_token
    if (!token || !data.user) throw new Error('Password updated but no session was returned')
    return token
  },

  /**
   * Bring the Supabase password in line after the API changed the platform one.
   *
   * Only when this browser is in a Supabase session — that session is what authorises the
   * update, and an artist without one has no Supabase password the login could still accept.
   * Best-effort and never throws: the API change already succeeded, and the server syncs linked
   * accounts itself once it has the service-role key.
   *
   * Only for the account whose password just changed. During "View as artist" the API token is
   * the artist's but this browser's Supabase session is still the MANAGER's, so an unguarded
   * update would silently reset the manager's own password. The session's email must match.
   */
  async updatePasswordIfSignedIn(
    password: string,
    accountEmail: string | null | undefined
  ): Promise<'updated' | 'no-session' | 'not-this-account' | 'failed'> {
    try {
      const supabase = getSupabase()
      if (!supabase) return 'no-session'
      const { data } = await supabase.auth.getSession()
      if (!data.session) return 'no-session'
      const sessionEmail = (data.session.user?.email || '').trim().toLowerCase()
      if (!accountEmail || sessionEmail !== accountEmail.trim().toLowerCase()) return 'not-this-account'
      const { error } = await supabase.auth.updateUser({ password })
      if (error) {
        console.warn('[Supabase] Password not updated:', error.message)
        return 'failed'
      }
      return 'updated'
    } catch (e) {
      console.warn('[Supabase] Password not updated:', e instanceof Error ? e.message : e)
      return 'failed'
    }
  },

  /** The session a redirect established, if any — used after a reset or provider round-trip. */
  async currentAccessToken(): Promise<string | null> {
    const supabase = getSupabase()
    if (!supabase) return null
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  },

  async signOut(): Promise<void> {
    const supabase = getSupabase()
    if (supabase) await supabase.auth.signOut()
  },

  loadPlatformUser,
}
